import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Chip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import EventIcon from "@mui/icons-material/Event";
import PeopleIcon from "@mui/icons-material/People";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import HistoryIcon from "@mui/icons-material/History";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import { getComplianceDashboard } from "api/onboardingApi";
import { getBrokerComplianceSummary } from "api/brokerApi";
import { unwrapList, unwrapObject } from "api/unwrap";

/**
 * Admin Compliance Dashboard.
 *
 * SPEC-002 Phase 5 (admin web). Read-only summary view showing where every
 * driver in the tenant stands across the onboarding pipeline + a heads-up on
 * trainings expiring soon.
 *
 * Powered by the backend's GET /api/v1/compliance/dashboard endpoint.
 */

const STEP_ORDER = [
  "DOCS_UPLOAD",
  "BACKGROUND_PENDING",
  "TRAINING_IN_PROGRESS",
  "DRUG_TEST_PENDING",
  "VEHICLE_INSPECTION_PENDING",
  "ESIGN_PENDING",
  "FINAL_REVIEW",
  "ONBOARDING_HOLD",
  "READY_TO_DRIVE",
  "REJECTED",
];

const STEP_LABELS = {
  DOCS_UPLOAD: "Docs Upload",
  BACKGROUND_PENDING: "Background Check",
  TRAINING_IN_PROGRESS: "Training",
  DRUG_TEST_PENDING: "Drug Test",
  VEHICLE_INSPECTION_PENDING: "Vehicle Inspection",
  ESIGN_PENDING: "E-Sign",
  FINAL_REVIEW: "Final Review",
  ONBOARDING_HOLD: "On Hold",
  READY_TO_DRIVE: "Ready to Drive",
  REJECTED: "Rejected",
};

const STEP_COLORS = {
  DOCS_UPLOAD: "#0ea5e9",
  BACKGROUND_PENDING: "#a855f7",
  TRAINING_IN_PROGRESS: "#f97316",
  DRUG_TEST_PENDING: "#facc15",
  VEHICLE_INSPECTION_PENDING: "#14b8a6",
  ESIGN_PENDING: "#8b5cf6",
  FINAL_REVIEW: "#3b82f6",
  ONBOARDING_HOLD: "#94a3b8",
  READY_TO_DRIVE: "#16a34a",
  REJECTED: "#dc2626",
};

const KpiCard = ({ label, value, hint, accent }) => (
  <Card variant="outlined" sx={{ borderRadius: 2 }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="h3"
        sx={{ fontWeight: 700, color: accent || "#0f172a", mt: 1 }}
      >
        {value}
      </Typography>
      {hint ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {hint}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

const StepRow = ({ step, count }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: 2,
      py: 1.5,
      borderBottom: "1px solid #e2e8f0",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: STEP_COLORS[step] || "#94a3b8",
        }}
      />
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {STEP_LABELS[step] || step}
      </Typography>
    </Box>
    <Chip
      label={count}
      size="small"
      sx={{
        fontWeight: 700,
        backgroundColor: count > 0 ? "#1F4E78" : "#e2e8f0",
        color: count > 0 ? "#ffffff" : "#64748b",
      }}
    />
  </Box>
);

export const CompliancePage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [brokerSummary, setBrokerSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, bs] = await Promise.all([
        getComplianceDashboard(),
        getBrokerComplianceSummary(),
      ]);
      setData(unwrapObject(res));
      setBrokerSummary(unwrapList(bs));
    } catch (e) {
      setError(e?.message || "Failed to load compliance dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const driverCounts = data?.driverCountByStep || {};
  const totalDrivers = Object.values(driverCounts).reduce(
    (sum, n) => sum + (n || 0),
    0,
  );
  const ready = driverCounts.READY_TO_DRIVE || 0;
  const blocked = data?.blockedDriversCount || 0;
  const expiringSoon = data?.expiringTrainingsNext30Days || 0;

  return (
    <Stack
      spacing={3}
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: "#f8fafc",
        height: "calc(100vh - 80px)",
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", md: "flex-start" },
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ flex: "1 1 auto", minWidth: 240 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Compliance Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Driver onboarding pipeline + training expiration health
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<ViewKanbanIcon />}
            onClick={() => navigate("/compliance/pipeline")}
            sx={{ backgroundColor: "#1F4E78" }}
          >
            Open Pipeline
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => navigate("/compliance/overview")}
            sx={{ backgroundColor: "#16a34a" }}
          >
            Audit Readiness
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EventIcon />}
            onClick={() => navigate("/compliance/renewals")}
          >
            Renewals
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PeopleIcon />}
            onClick={() => navigate("/compliance/drivers")}
          >
            All Drivers
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FactCheckIcon />}
            onClick={() => navigate("/compliance/pending-documents")}
          >
            Pending Docs
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => navigate("/compliance/activity")}
          >
            Activity
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DirectionsCarIcon />}
            onClick={() => navigate("/compliance/vehicles")}
          >
            Vehicles
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading && !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                label="Total drivers"
                value={totalDrivers}
                hint="Across all onboarding stages"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                label="Ready to drive"
                value={ready}
                hint={
                  totalDrivers > 0
                    ? `${Math.round((ready * 100) / totalDrivers)}% of fleet`
                    : "No drivers yet"
                }
                accent="#16a34a"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                label="Blocked drivers"
                value={blocked}
                hint="Cannot accept rides"
                accent={blocked > 0 ? "#dc2626" : "#0f172a"}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                label="Expiring (30d)"
                value={expiringSoon}
                hint="Training renewals needed"
                accent={expiringSoon > 0 ? "#d97706" : "#0f172a"}
              />
            </Grid>
          </Grid>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Onboarding pipeline
              </Typography>
              <Box>
                {STEP_ORDER.map((step) => (
                  <StepRow
                    key={step}
                    step={step}
                    count={driverCounts[step] || 0}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>

          {brokerSummary.length > 0 ? (
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Compliance by broker
                </Typography>
                <Box>
                  {brokerSummary.map((b) => {
                    const pctColor =
                      b.percent >= 90 ? "#16a34a" :
                      b.percent >= 70 ? "#f97316" : "#dc2626";
                    return (
                      <Box
                        key={b.brokerId}
                        sx={{
                          py: 1.25,
                          borderBottom: "1px solid #f1f5f9",
                          "&:last-child": { borderBottom: "none" },
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "#f8fafc" },
                        }}
                        onClick={() => navigate(`/brokers/${b.brokerId}/compliance`)}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                            {b.brokerName}
                          </Typography>
                          <Chip
                            size="small"
                            label={b.brokerCode}
                            sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 700, color: pctColor, minWidth: 48, textAlign: "right" }}>
                            {b.percent}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70, textAlign: "right" }}>
                            {b.compliantDrivers} / {b.totalDrivers}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            height: 6,
                            backgroundColor: "#e2e8f0",
                            borderRadius: 3,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${b.percent}%`,
                              backgroundColor: pctColor,
                              transition: "width 0.3s",
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          ) : null}

          <Typography variant="caption" color="text.secondary">
            Click a broker bar to open its compliance report. Click any KPI button to drill into the pipeline.
          </Typography>
        </>
      )}
    </Stack>
  );
};

export default CompliancePage;
